/**
 * publishToMedia — CORE-588 orchestration for «Публикация в Медиа».
 *
 * Extracted from PublishToMediaModal so the two-step publish sequence is a
 * plain, unit-testable function. Owner decision (CORE-588): «Медиа» is the
 * existing recording→VideoPractice promotion, so publishing is:
 *
 *   1. persist the метаданные (title/description/cover/visibility) onto the
 *      room archive — CORE-115 propagates them onto the promoted VideoPractice;
 *   2. promote the Ready recording into the Медиа library (idempotent).
 *
 * The ordering matters: metadata must land BEFORE the promote so the created
 * VideoPractice carries the master's edited название/описание/обложка. If the
 * archive update fails we do NOT promote — a half-published recording with the
 * wrong title is worse than a clean retry.
 *
 * CORE-1762 (QA george 31.10.2026): the archive's visibility does NOT reach the
 * promoted copy — «Только подписчики» came out as a video visible to all, with a
 * working address for guests. The form promises one visibility for the recording
 * and its copy, so step 3 sets the chosen audience on every promoted video
 * (`PUT /api/master/content/videos/{id}`, the call QA used); if that fails for a
 * restricted choice the copy is hidden again (unpublish) and the error is
 * reported — a public copy nobody asked for must not stay behind.
 */
import { api } from '../../services/api';
import type { VideoPracticeVisibility, WatchPartyVisibility } from '../../types/auth';

/** The recording's audience as the Медиа copy spells it. */
export function videoVisibilityOf(visibility: WatchPartyVisibility): VideoPracticeVisibility {
  return visibility === 'Public' ? 'all' : visibility === 'Subscribers' ? 'subscribers' : 'private';
}

/** The promoted videos: the first stretch and every further one (CORE-760), once each. */
export function promotedVideoIds(res: { videoPracticeId: number; items?: Array<{ videoPracticeId: number }> }): number[] {
  const ids = [res.videoPracticeId, ...(res.items ?? []).map((i) => i.videoPracticeId)].filter((id) => Number.isFinite(id) && id > 0);
  return [...new Set(ids)];
}

export interface PublishMediaMeta {
  title?: string;
  description?: string;
  coverImageUrl?: string | null;
  visibility: WatchPartyVisibility;
}

export async function publishRecordingToMedia(
  roomId: number,
  meta: PublishMediaMeta,
): Promise<{ videoPracticeId: number; alreadyPromoted: boolean }> {
  // 1. Metadata first (blank strings are treated as «unchanged» → omitted).
  await api.updateArchive(roomId, {
    title: meta.title?.trim() || undefined,
    description: meta.description?.trim() || undefined,
    coverImageUrl: meta.coverImageUrl ?? undefined,
    visibility: meta.visibility,
  });
  // 2. Promote the Ready recording into Медиа (idempotent — a repeat returns
  //    the same VideoPractice with alreadyPromoted=true).
  const res = await api.promoteRoomRecordingToVideo(roomId);
  // 3. CORE-1762 — the copy takes the chosen audience; a Медиа video is born «all».
  const visibility = videoVisibilityOf(meta.visibility);
  try {
    await Promise.all(promotedVideoIds(res).map((id) => api.updateVideo(id, { visibility })));
  } catch (e) {
    if (visibility !== 'all') {
      // Not the audience the master chose: hide the copy rather than leave it open to everyone.
      await api.unpublishRoomRecording(roomId).catch(() => undefined);
      throw new Error('Не удалось ограничить доступ к записи в «Медиа», публикация отменена. Попробуйте ещё раз.', { cause: e });
    }
    throw e;
  }
  return res;
}
